import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Col, Form, Input, Label, Row, Spinner } from 'reactstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import sanitizeHtml from 'sanitize-html';
import 'react-datepicker/dist/react-datepicker.css';

import { ApplicationState } from 'store/redux';
import * as AuthenticationStore from 'store/redux/Authentication';
import { IAuthenticationActionCreators, UserState } from 'store/redux/Authentication';
import Context from 'configuration/context';
import { orderServiceFactory } from 'services/orderService';
import userServiceFactory, { IAEHierarchyItem, IHierarchyItem } from 'services/userService';
import { authenticationServiceFactory } from 'services/authenticationService';
import { telemetry } from 'services/telemery';
import { IOrder, OrderState, SaveOrderResponse } from 'models/Order';
import { LoadState, SubmissionState } from 'constants/states';
import { getDefaultOrderModel, orderDefaultState } from 'constants/defaultStates';
import { ToOrderModel } from 'helpers/toOrderModel';
import { downloadAttachment } from 'helpers/downloadAttachment';
import { mapOrder } from 'helpers/mapOrder';
import { Flatten } from 'helpers/flatten';
import { ToOrderReference } from 'helpers/toOrderReference';
import { IOrderModel } from 'models/Order';
import { IOption } from 'models/Form';
import { orderDefinition } from 'definitions/orderDefinition';
import {
  ResultModal,
  OrderGrid,
  OrderFileUpload,
  OrderFileList,
  FormSubmitButton,
} from 'components';
import {
  ControlledRadioButtons,
  ControlledSelect,
  ControlledDatePickerInput,
  ControlledTextArea,
  ControlledTextInput,
} from 'components/form';
import './Orders.css';

export type IOrderProps = IAuthenticationActionCreators & {
  authenticationState?: UserState;
};

dayjs.extend(utc);

const Order = ({ authenticationState, setLoginState }: IOrderProps) => {
  const { t, ready } = useTranslation('translation');
  const { pathname } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = authenticationState?.UserInRole;
  const urlSegments = document.location.href.split('/');
  const defaultHealthCenterId = currentUser?.selectedCustomer?.defaultHealthCenter;
  const [orderState, setOrderState] = useState<OrderState>(orderDefaultState);
  const healthCenter = orderState.aEHierarchyOptions.find(
    (item: any) => item.value === defaultHealthCenterId
  );
  const {
    reset,
    watch,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<IOrderModel>({
    defaultValues: getDefaultOrderModel(currentUser, urlSegments[5]),
    mode: 'all',
  });
  const isCopyOrder: boolean = pathname.indexOf('/copy') >= 0;
  const formIsReadOnly: boolean = !!id && !isCopyOrder;
  let isIndividualOrder: boolean = urlSegments[5] === 'individual';
  let isGroupOrder: boolean = urlSegments[5] === 'group';
  let isEducationOrder: boolean = urlSegments[5] === 'education';
  const loginState = authenticationState?.LoginState;
  let order: IOrderModel = watch();
  const currentCustomer = currentUser && currentUser.selectedCustomer?.customerId;

  const transformCustomersToCostBearerOptionsList = useCallback((items: IHierarchyItem[]) => {
    const o = [] as IOption<string>[];
    items.map((i) => Flatten(i, o));
    return o;
  }, []);

  // Check if the order belongs to the selected customer
  useEffect(() => {
    if (order.id !== undefined && Number(currentCustomer) !== order.customerId) {
      navigate('/service');
    }
  }, [order, currentCustomer]);

  // Load order
  useEffect(() => {
    // Anything in here is fired on component mount.
    if (
      loginState &&
      loginState === AuthenticationStore.LoginState.LOGGED_IN &&
      id &&
      orderState.aeHierarchyLoadState === LoadState.LOADED &&
      orderState.hierarchyLoadState === LoadState.LOADED
    ) {
      setOrderState((prevState) => ({
        ...prevState,
        orderLoadState: LoadState.LOADING,
      }));
      orderServiceFactory(
        Context.APIUrl + '/v1',
        authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState)
      )
        .getOrder(id)
        .then((_order: IOrder) => {
          const orderModel = ToOrderModel(_order);
          if (isCopyOrder) {
            orderModel.customerName = currentUser ? currentUser?.name : '';
            orderModel.customerContactEmail =
              currentUser && currentUser?.email ? currentUser?.email : '';
            orderModel.attachments = [];
            orderModel.requestedDate =
              orderModel.requestedDate === null
                ? null
                : dayjs(new Date()).add(5, 'd').toDate().toISOString();
            orderModel.bookingDetails = '';
            orderModel.orderStatus = 0;
          }
          reset(orderModel);
          setOrderState((prevState) => ({
            ...prevState,
            orderLoadState: LoadState.LOADED,
            readOnlyOrder: _order,
          }));
        })
        .catch((error: any) => {
          setOrderState((prevState) => ({
            ...prevState,
            orderLoadState: LoadState.ERROR,
          }));
        });
    }
  }, [
    loginState,
    id,
    orderState.aeHierarchyLoadState,
    orderState.hierarchyLoadState,
    reset,
    currentUser,
    isCopyOrder,
    authenticationState,
    setLoginState,
  ]);

  useEffect(() => {
    // Anything in here is fired on component mount.
    if (ready) {
      setOrderState((prevState) => ({
        ...prevState,
        feedbackOptions: [
          { label: t('orders.OPTION_FEEDBACK_METHOD_YES'), value: 1 },
          { label: t('orders.OPTION_FEEDBACK_METHOD_NO'), value: 0 },
        ],
      }));
    }
  }, [ready, setValue, t, order.feedbackPerformed]);

  useEffect(() => {
    // Anything in here is fired on component mount.
    if (
      loginState &&
      loginState === AuthenticationStore.LoginState.LOGGED_IN &&
      currentUser &&
      currentUser.email &&
      currentUser.selectedCustomer
    ) {
      const service = userServiceFactory(
        Context.APIUrl + '/v1',
        authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState)
      );
      setOrderState((prevState) => ({
        ...prevState,
        aeHierarchyLoadState: LoadState.LOADING,
      }));
      service
        .GetAEHierarchy()
        .then((hierarchy: IAEHierarchyItem[]) => {
          setOrderState((prevState) => ({
            ...prevState,
            aEHierarchyOptions: hierarchy.map((i) => {
              return { value: i.id, label: i.name };
            }),
            aeHierarchyLoadState: LoadState.LOADED,
          }));
          const defaultPerformingUnit = hierarchy.filter((f) => f.default);
          reset({
            ...order,
            healthCenter:
              defaultPerformingUnit.length == 1 ? defaultPerformingUnit[0] : order.healthCenter,
          });
        })
        .catch((error: any) => {
          setOrderState((prevState) => ({
            ...prevState,
            aEHierarchyOptions: [],
            aeHierarchyLoadState: LoadState.ERROR,
          }));
        });

      setOrderState((prevState) => ({
        ...prevState,
        aeHierarchyLoadState: LoadState.LOADING,
      }));
      service
        .GetRelatedCustomerHierarchy(currentUser?.selectedCustomer?.customerId.toString())
        .then((hierarchy: IHierarchyItem[]) => {
          let options = transformCustomersToCostBearerOptionsList(hierarchy);
          setOrderState((prevState) => ({
            ...prevState,
            hierarchyOptions: options,
            hierarchyLoadState: LoadState.LOADED,
          }));
        })
        .catch((error: any) => {
          setOrderState((prevState) => ({
            ...prevState,
            hierarchyOptions: [],
            hierarchyLoadState: LoadState.ERROR,
          }));
        });
    }
  }, [
    loginState,
    currentUser,
    setValue,
    transformCustomersToCostBearerOptionsList,
    authenticationState,
    setLoginState,
  ]);

  useEffect(() => {
    setValue(
      'requestedDate',
      order.requestedDeliveryDateSpecification.id === 'asap' ? null : order.requestedDate
    );
  }, [order.requestedDeliveryDateSpecification]);

  useEffect(() => {
    if (healthCenter) {
      setValue('healthCenter', { id: healthCenter?.value, name: healthCenter?.label });
    }
  }, [orderState.aEHierarchyOptions]);

  const removeAttachment = (index: number) => {
    setValue(
      'attachments',
      order.attachments.filter((_, _index) => {
        return index !== _index;
      })
    );
  };

  const onSubmit = (order: IOrderModel) => {
    if (order.healthCenter.name === '-1') {
      setOrderState((prevState) => ({
        ...prevState,
        showResultModal: true,
        submissionState: SubmissionState.SUBMITTED,
      }));
      return;
    }

    const service = orderServiceFactory(
      Context.APIUrl + '/v1',
      authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState)
    );

    setOrderState((prevState) => ({
      ...prevState,
      showResultModal: true,
      submissionState: SubmissionState.SUBMITTING,
    }));

    service.saveOrder(mapOrder(order, urlSegments[5]), order.attachments).then(
      (orderResponse: SaveOrderResponse) => {
        setOrderState((prevState) => ({
          ...prevState,
          submissionState: SubmissionState.SUBMITTED,
        }));

        telemetry.trackEvent(
          { name: 'Order submission succeeded' },
          { customer: currentUser?.selectedCustomer?.customerId }
        );
      },
      (error: any) => {
        telemetry.trackEvent(
          { name: 'Order submission failed' },
          {
            customer: currentUser?.selectedCustomer?.customerId,
            error: JSON.stringify(error),
          }
        );
        setOrderState((prevState) => ({
          ...prevState,
          submissionState: SubmissionState.ERROR,
        }));
      }
    );
  };

  const digitalDeliveryOptions = [
    {
      label: t('orders.OPTION_DIGITAL_DELIVERY_YES'),
      value: 'yes',
    },
    {
      label: t('orders.OPTION_DIGITAL_DELIVERY_NO'),
      value: 'no',
    },
  ];

  //Show details for booked/partner booked or cancelled
  const showBookingDetails =
    order.orderStatus &&
    (order.orderStatus === 1 || order.orderStatus === 2 || order.orderStatus === 3);

  return (
    <div className="order-form limited">
      {((id && orderState.orderLoadState === LoadState.LOADING) ||
        orderState.aeHierarchyLoadState === LoadState.LOADING ||
        orderState.hierarchyLoadState === LoadState.LOADING) && (
        <h1 className="loading-indicator-container col-12">
          <Spinner /> {t('orders.FETCHING_ORDER')}...
        </h1>
      )}
      {(orderState.aeHierarchyLoadState === LoadState.ERROR ||
        orderState.hierarchyLoadState === LoadState.ERROR ||
        orderState.orderLoadState === LoadState.ERROR) && (
        <h1 className="loading-indicator-container col-12">
          Error! Please reload page! If it doesn't help, try logging out and logging in again.
        </h1>
      )}
      {orderState.aeHierarchyLoadState === LoadState.LOADED &&
        orderState.hierarchyLoadState === LoadState.LOADED &&
        (!id || orderState.orderLoadState === LoadState.LOADED) && (
          <>
            {isIndividualOrder && <h1>{t('orders.TYPE_Individual_FULL')}</h1>}
            {isGroupOrder && <h1>{t('orders.TYPE_Group_FULL')}</h1>}
            {isEducationOrder && <h1>{t('orders.TYPE_Education_FULL')}</h1>}
            <Form
              key={order.orderNumber}
              onSubmit={handleSubmit(
                (order, event) => {
                  event && event.preventDefault();
                  event && event.stopPropagation();
                  onSubmit(order);
                },
                (e) => {
                  console.error(e);
                }
              )}
            >
              <fieldset>
                {!formIsReadOnly && (
                  <Row>
                    <Col className="col-12">
                      <strong>{t('orders.MANDATORY_FIELDS_INSTRUCTIONS')}</strong>
                    </Col>
                  </Row>
                )}
                {formIsReadOnly && (
                  <OrderGrid
                    control={control}
                    definitions={orderDefinition.orderInfo}
                    formIsReadOnly={formIsReadOnly}
                  />
                )}
                <OrderGrid
                  control={control}
                  definitions={orderDefinition.customerInfoFirst}
                  formIsReadOnly={formIsReadOnly}
                />
                <Row>
                  <Label className="col-md-6 mt-4">
                    <ControlledTextInput<IOrderModel>
                      control={control}
                      fieldName="customerContactEmail"
                      readonly={formIsReadOnly}
                      label={t('orders.FIELDLABEL_ORDER_EMAIL') + '*'}
                      placeholder={t('orders.FIELD_PLACEHOLDER_ORDER_EMAIL')}
                      rules={{
                        required: true,
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: '',
                        },
                      }}
                    />
                  </Label>
                  <Label className="col-md-6 mt-4">
                    <ControlledSelect<IOrderModel, string>
                      getOptionLabel={(item: IOption<string>) =>
                        (item.recursionLevel ? '\u00A0'.repeat(item.recursionLevel * 3) : '') +
                        item.label
                      }
                      getOptionValue={(item: IOption<string>) => item.value}
                      options={orderState.hierarchyOptions}
                      rules={{ required: true }}
                      control={control}
                      fieldName="customerUnit"
                      readonly={formIsReadOnly}
                      placeholder={t('orders.FIELD_PLACEHOLDER_COST_BEARING_UNIT')}
                      label={t('orders.FIELDLABEL_COST_BEARING_UNIT') + '*'}
                    />
                  </Label>
                </Row>
                <hr className="mt-4" />
                {(isIndividualOrder ||
                  (orderState.orderLoadState === LoadState.LOADED && order.orderType === 0)) && (
                  <OrderGrid
                    control={control}
                    definitions={orderDefinition.employeeInfo}
                    formIsReadOnly={formIsReadOnly}
                  />
                )}
                {(isGroupOrder ||
                  (orderState.orderLoadState === LoadState.LOADED && order.orderType === 2)) && (
                  <OrderGrid
                    control={control}
                    definitions={orderDefinition.groupInfo}
                    formIsReadOnly={formIsReadOnly}
                  />
                )}
                {(isEducationOrder ||
                  (orderState.orderLoadState === LoadState.LOADED && order.orderType === 1)) && (
                  <OrderGrid
                    control={control}
                    definitions={orderDefinition.educationInfo}
                    formIsReadOnly={formIsReadOnly}
                  />
                )}
                <hr className="mt-4" />
                <OrderGrid
                  control={control}
                  definitions={orderDefinition.descriptionInfo}
                  formIsReadOnly={formIsReadOnly}
                />
                <Row>
                  <Label className="col-md-6 mt-4">
                    <ControlledTextArea<IOrderModel>
                      control={control}
                      readonly={formIsReadOnly}
                      fieldName="requestedAdditionalInformation"
                      label={t('orders.FIELDLABEL_OTHER_INFORMATION')}
                    />
                  </Label>
                  <div className="col-md-6 mt-4">
                    {formIsReadOnly &&
                      orderState.readOnlyOrder &&
                      orderState.readOnlyOrder.attachments &&
                      !!orderState.readOnlyOrder.attachments.length && (
                        <OrderFileList
                          attachments={orderState.readOnlyOrder.attachments}
                          downloadAttachment={(attachmentId, attachmentTitle) =>
                            downloadAttachment(
                              order.id,
                              attachmentTitle,
                              'order',
                              attachmentId,
                              authenticationState,
                              setLoginState
                            )
                          }
                        />
                      )}
                    {!formIsReadOnly && (
                      <OrderFileUpload
                        order={order}
                        control={control}
                        formIsReadOnly={formIsReadOnly}
                        removeAttachment={(index: number) => removeAttachment(index)}
                      />
                    )}
                  </div>
                  <div className="col-md-12 field-description">
                    {t('orders.FIELD_DESCRIPTION_OTHER_INFORMATION')}
                  </div>
                </Row>
                <Row>
                  <Label className="col-md-6 mt-4">
                    {(!formIsReadOnly ||
                      order.requestedDeliveryDateSpecification.id === 'asap') && (
                      <ControlledSelect<IOrderModel, string>
                        rules={{ required: order.requestedDeliveryDateSpecification.id === 'asap' }}
                        control={control}
                        fieldName="requestedDeliveryDateSpecification"
                        placeholder={t('orders.FIELD_PLACEHOLDER_SPECIFIC_FEEDBACK_RECIPIENT')}
                        getOptionLabel={(item: IOption<string>) => item.label}
                        getOptionValue={(item: IOption<string>) => item.value.toString()}
                        readonly={formIsReadOnly}
                        label={t('orders.FIELDLABEL_REQUESTED_DELIVERY_DATE') + '*'}
                        options={[
                          { label: t('orders.OPTION_REQUESTED_DELIVERYDATE_ASAP'), value: 'asap' },
                          {
                            label: t('orders.OPTION_REQUESTED_DELIVERYDATE_SPECIFIC_DATE'),
                            value: 'specificDate',
                          },
                        ]}
                      />
                    )}
                    {order.requestedDeliveryDateSpecification.id === 'specificDate' && (
                      <ControlledDatePickerInput<IOrderModel>
                        rules={{
                          required: order.requestedDeliveryDateSpecification.id === 'specificDate',
                        }}
                        control={control}
                        fieldName="requestedDate"
                        readonly={formIsReadOnly}
                        placeholder={t('orders.FIELD_PLACEHOLDER_REQUESTED_DELIVERY_DATE')}
                        label={t('orders.FIELDLABEL_REQUESTED_DELIVERY_DATE') + '*'}
                        minDate={dayjs(new Date())
                          .add(isCopyOrder ? 5 : 14, 'd')
                          .toDate()}
                      />
                    )}
                  </Label>
                  <Label className="col-md-6 mt-4">
                    <ControlledRadioButtons<IOrderModel, string>
                      rules={{ required: true }}
                      transform={{
                        toFormValue: (i) => i.value,
                        toSelectedValue: (i) => ({
                          label: i.toString(),
                          value: i,
                        }),
                        selectedValueToString: (i) => i.value.toString(),
                        formValueToString: (o) => o.toString(),
                      }}
                      control={control}
                      readonly={formIsReadOnly}
                      fieldName="digitalDelivery"
                      options={digitalDeliveryOptions}
                      defaultOption={digitalDeliveryOptions[0]}
                      label={t('orders.FIELDLABEL_DIGITAL_DELIVERY') + '*'}
                    />
                  </Label>
                </Row>
                <Row>
                  <Label className="col-md-6 mt-4">
                    <ControlledSelect<IOrderModel, number>
                      rules={{ required: true }}
                      placeholder={t('orders.OPTION_FEEDBACK_METHOD_PLACEHOLDER')}
                      control={control}
                      fieldName="feedbackPerformed"
                      getOptionLabel={(item: IOption<number>) => item.label}
                      getOptionValue={(item: IOption<number>) => item.value.toString()}
                      readonly={formIsReadOnly}
                      options={orderState.feedbackOptions}
                      isOnlyValue
                      label={t('orders.FIELDLABEL_FEEDBACK_METHOD') + '*'}
                    />
                    <span className="field-description">
                      {t('orders.OPTION_FEEDBACK_METHOD_YES_DESCRIPTION')}
                    </span>
                  </Label>
                  {order.feedbackPerformed === 1 && (
                    <Label className="col-md-6 mt-4">
                      <ControlledSelect<IOrderModel, string>
                        rules={{ required: order.feedbackPerformed === 1 }}
                        control={control}
                        fieldName="feedbackRecipient"
                        placeholder={t('orders.FIELD_PLACEHOLDER_SPECIFIC_FEEDBACK_RECIPIENT')}
                        getOptionLabel={(item: IOption<string>) => item.label}
                        getOptionValue={(item: IOption<string>) => item.value.toString()}
                        readonly={formIsReadOnly}
                        options={[
                          {
                            label: t('orders.OPTION_SPECIFIC_FEEDBACK_RECIPIENT_YES'),
                            value: 'yes',
                          },
                          {
                            label: t('orders.OPTION_SPECIFIC_FEEDBACK_RECIPIENT_NO'),
                            value: 'no',
                          },
                        ]}
                        label={t('orders.FIELDLABEL_SPECIFIC_FEEDBACK_RECIPIENT') + '*'}
                      />
                      {order.feedbackRecipient && order.feedbackRecipient.id === 'yes' && (
                        <ControlledTextArea<IOrderModel>
                          rules={{
                            required: true,
                          }}
                          control={control}
                          fieldName="feedbackContactDetails"
                          readonly={formIsReadOnly}
                          placeholder={t(
                            'orders.FIELD_PLACEHOLDER_SPECIFIC_FEEDBACK_RECIPIENT_DETAILS'
                          )}
                          label={
                            t('orders.FIELD_PLACEHOLDER_SPECIFIC_FEEDBACK_RECIPIENT_DETAILS') + '*'
                          }
                        />
                      )}
                    </Label>
                  )}
                </Row>
                {showBookingDetails ? (
                  <Row>
                    <Label className="col-md-6 mt-4">
                      {order.orderStatus === 2
                        ? t('orders.CANCEL_DETAILS')
                        : t('orders.BOOKING_DETAILS')}
                      <div
                        className="fake form-control"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(order.bookingDetails),
                        }}
                      />
                    </Label>
                    {order.orderStatus === 1 && (order.orderType === 2 || order.bookingDate) && (
                      <Label className="col-md-6 mt-4">
                        {t('orders.SCHEDULED_DATE')}
                        {order.orderType === 2 ? (
                          <div className="fake form-control">
                            {t('orders.GROUP_DATE_DISTRIBUTED')}
                          </div>
                        ) : (
                          <Input
                            disabled
                            className="form-control"
                            readOnly={true}
                            //The booking date is saved without offsets, so we use UTC to also display it without any offsets
                            value={dayjs.utc(order.bookingDate).format('YYYY-MM-DD HH:mm')}
                          />
                        )}
                      </Label>
                    )}
                  </Row>
                ) : (
                  <></>
                )}
                <Row>
                  <Label className="col-md-6 mt-4">
                    <ControlledSelect<IOrderModel, string>
                      rules={{ required: true }}
                      control={control}
                      fieldName="healthCenter"
                      options={orderState.aEHierarchyOptions}
                      getOptionLabel={(item: IOption<string>) => item.label}
                      getOptionValue={(item: IOption<string>) => item.value}
                      readonly={formIsReadOnly}
                      placeholder={t('orders.FIELD_PLACEHOLDER_PREFERRED_PERFORMING_UNIT')}
                      label={t('orders.FIELDLABEL_PREFERRED_PERFORMING_UNIT') + '*'}
                    />
                  </Label>
                  {(isEducationOrder || isGroupOrder) && (
                    <Label className="col-md-6 mt-4">
                      <ControlledSelect<IOrderModel, string>
                        isMulti={true}
                        control={control}
                        fieldName="additionalHealthCenters"
                        options={orderState.aEHierarchyOptions}
                        getOptionLabel={(item: IOption<string>) => item.label}
                        getOptionValue={(item: IOption<string>) => item.value}
                        readonly={formIsReadOnly}
                        placeholder={t('orders.FIELD_PLACEHOLDER_PREFERRED_PERFORMING_UNIT')}
                        label={t('orders.FIELDLABEL_ADDITIONAL_PERFORMING_UNITS')}
                      />
                    </Label>
                  )}
                </Row>
                {!formIsReadOnly && (
                  <FormSubmitButton
                    value="orders.ORDER_SUBMIT"
                    placeholder="orders.ORDER_SUBMIT"
                    isDisabled={orderState.submissionState !== SubmissionState.IDLE}
                    description="orders.ORDER_RULES_AND_CONDITIONS"
                  />
                )}
              </fieldset>
            </Form>
          </>
        )}
      {orderState.submissionState !== SubmissionState.IDLE && (
        <ResultModal
          isOpen={orderState.showResultModal}
          onClose={() =>
            setOrderState((prevState) => ({
              ...prevState,
              showResultModal: false,
            }))
          }
          state={orderState.submissionState}
        />
      )}
    </div>
  );
};

export default connect(
  (appState: ApplicationState) => ({ authenticationState: appState.authentication }),
  { ...AuthenticationStore.actions }
)(Order);
